import { useCallback, useEffect, useRef, useState } from 'react';
import { Text } from '@hopskipdrive/react-components';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Drawer, IconButton, useMediaQuery } from '@mui/material';

import { Button } from '../Button';

import { styles } from './FilterDrawer.styles';
import { FilterDrawerSection } from './FilterDrawerSection';

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterSection {
  key: string;
  label: string;
  options: FilterOption[];
  selected: string[];
  searchable?: boolean;
  searchPlaceholder?: string;
  onSearch?: (query: string) => void;
  isSearching?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoading?: boolean;
  emptyMessage?: string;
}

export interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  sections: FilterSection[];
  onApply: (selections: Record<string, string[]>) => void;
  onClearAll: () => void;
  animateSections?: boolean;
}

export const FilterDrawer = ({
  open,
  onClose,
  sections,
  onApply,
  onClearAll,
  animateSections = false,
}: FilterDrawerProps) => {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [draft, setDraft] = useState<Record<string, string[]>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const sectionsRef = useRef(sections);

  sectionsRef.current = sections;

  useEffect(() => {
    if (!open) {
      return;
    }
    setDraft(Object.fromEntries(sectionsRef.current.map((s) => [s.key, [...s.selected]])));
    setSearchQueries({});
    setExpanded({});
  }, [open]);

  const toggleSection = useCallback((key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleCheckbox = useCallback((sectionKey: string, value: string, checked: boolean) => {
    setDraft((prev) => ({
      ...prev,
      [sectionKey]: checked
        ? [...(prev[sectionKey] ?? []), value]
        : (prev[sectionKey] ?? []).filter((v) => v !== value),
    }));
  }, []);

  const handleSearch = useCallback((section: FilterSection, query: string) => {
    setSearchQueries((prev) => ({ ...prev, [section.key]: query }));
    section.onSearch?.(query);
  }, []);

  const handleClearAll = useCallback(() => {
    setDraft(Object.fromEntries(sections.map((s) => [s.key, []])));
    onClearAll();
  }, [sections, onClearAll]);

  const handleApply = useCallback(() => {
    onApply(draft);
    onClose();
  }, [draft, onApply, onClose]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: [styles.paper, animateSections && { maxWidth: '100vw' }] }}
    >
      <Box sx={styles.header}>
        <Text sx={styles.title}>Filters</Text>
        <IconButton onClick={onClose} sx={styles.closeButton} aria-label="Close filters" size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={styles.sectionsContainer}>
        {sections.map((section) => (
          <FilterDrawerSection
            key={section.key}
            section={section}
            selected={draft[section.key] ?? []}
            searchQuery={searchQueries[section.key] ?? ''}
            expanded={expanded[section.key] ?? false}
            animateSections={animateSections}
            prefersReducedMotion={prefersReducedMotion}
            onToggle={() => toggleSection(section.key)}
            onCheckbox={(value, checked) => handleCheckbox(section.key, value, checked)}
            onSearch={(query) => handleSearch(section, query)}
          />
        ))}
      </Box>

      <Box sx={styles.footer}>
        <Box sx={styles.footerContent}>
          <Button color="tertiary" onClick={handleClearAll} sx={{ minWidth: 0, p: 0 }}>
            Clear all
          </Button>
          <Button color="primary" onClick={handleApply}>
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
